import { computed, type Ref } from "vue";

import {
  type ChartIndex,
  type SavedChartSetting,
} from "shared/modules/Charts/types";
import { useInsightsChartWidth } from "shared/modules/InsightsChart/composables/useInsightsChartWidth";
import { type Maybe } from "shared/types";

interface UseInsightsDashboardChartOrderingProps {
  availableCharts: Ref<Maybe<ChartIndex>>;
  charts: Ref<SavedChartSetting[]>;
}

interface IsInsightsChartFullWidthOptions {
  availableCharts?: Maybe<ChartIndex>;
  chartSetting: SavedChartSetting;
  chartSettingsLength: number;
  isChartRangeDoubleWidth: ({
    interval,
    range,
  }: {
    interval?: SavedChartSetting["interval"];
    range: SavedChartSetting["range"];
  }) => boolean;
  isChartStreamsDoubleWidth?: (setting: SavedChartSetting) => boolean;
}

interface GetInsightsDashboardOrderedChartsOptions {
  availableCharts?: Maybe<ChartIndex>;
  charts: SavedChartSetting[];
  isChartRangeDoubleWidth: ({
    interval,
    range,
  }: {
    interval?: SavedChartSetting["interval"];
    range: SavedChartSetting["range"];
  }) => boolean;
  isChartStreamsDoubleWidth?: (setting: SavedChartSetting) => boolean;
}

export function isInsightsChartFullWidth({
  availableCharts,
  chartSetting,
  chartSettingsLength,
  isChartRangeDoubleWidth,
  isChartStreamsDoubleWidth,
}: IsInsightsChartFullWidthOptions): boolean {
  const chartStreamsDoubleWidth = isChartStreamsDoubleWidth ?? (() => false);

  const fullWidth = (chartSetting as { full_width?: boolean }).full_width;

  if (fullWidth !== undefined) {
    return fullWidth;
  }

  if (chartSettingsLength === 1) {
    return true;
  }

  if (chartStreamsDoubleWidth(chartSetting)) {
    return true;
  }

  const chartConfig = availableCharts?.[chartSetting.chart];
  const supportsDoubleWidth = Boolean(chartConfig?.has_double_width);

  if (!supportsDoubleWidth) {
    return false;
  }

  return isChartRangeDoubleWidth({
    interval: chartSetting.interval,
    range: chartSetting.range,
  });
}

export function getInsightsDashboardOrderedCharts({
  availableCharts,
  charts,
  isChartRangeDoubleWidth,
  isChartStreamsDoubleWidth,
}: GetInsightsDashboardOrderedChartsOptions): SavedChartSetting[] {
  const isChartFullWidth = (chartSetting: SavedChartSetting): boolean =>
    isInsightsChartFullWidth({
      availableCharts,
      chartSetting,
      chartSettingsLength: charts.length,
      isChartRangeDoubleWidth,
      isChartStreamsDoubleWidth,
    });

  const packedChartOrder = (
    orderedCharts: SavedChartSetting[]
  ): SavedChartSetting[] => {
    const packedCharts: SavedChartSetting[] = [];
    let delayedFullWidthCharts: SavedChartSetting[] = [];
    let pendingHalfWidthChart: SavedChartSetting | undefined;

    orderedCharts.forEach((chart) => {
      if (isChartFullWidth(chart)) {
        if (pendingHalfWidthChart) {
          delayedFullWidthCharts.push(chart);
        } else {
          packedCharts.push(chart);
        }

        return;
      }

      if (pendingHalfWidthChart) {
        packedCharts.push(pendingHalfWidthChart, chart);
        packedCharts.push(...delayedFullWidthCharts);
        pendingHalfWidthChart = undefined;
        delayedFullWidthCharts = [];

        return;
      }

      pendingHalfWidthChart = chart;
    });

    packedCharts.push(...delayedFullWidthCharts);

    if (pendingHalfWidthChart) {
      packedCharts.push(pendingHalfWidthChart);
    }

    return packedCharts;
  };

  const pairSortedCharts = charts.flatMap((pair, index, source) => {
    if (index % 2 === 1) {
      return [];
    }

    return source
      .slice(index, index + 2)
      .sort(
        (left, right) =>
          Number(isChartFullWidth(right)) - Number(isChartFullWidth(left))
      );
  });

  return packedChartOrder(pairSortedCharts);
}

export function useInsightsDashboardChartOrdering({
  availableCharts,
  charts,
}: UseInsightsDashboardChartOrderingProps) {
  const { isChartRangeDoubleWidth, isChartStreamsDoubleWidth } =
    useInsightsChartWidth();

  function isChartFullWidth(chartSetting: SavedChartSetting): boolean {
    return isInsightsChartFullWidth({
      availableCharts: availableCharts.value,
      chartSetting,
      chartSettingsLength: charts.value.length,
      isChartRangeDoubleWidth,
      isChartStreamsDoubleWidth,
    });
  }

  const orderedCharts = computed<SavedChartSetting[]>(() =>
    getInsightsDashboardOrderedCharts({
      availableCharts: availableCharts.value,
      charts: charts.value,
      isChartRangeDoubleWidth,
      isChartStreamsDoubleWidth,
    })
  );

  return {
    isChartFullWidth,
    orderedCharts,
  };
}
